import { memoryStore, useStore } from "../../../lib/store";

/**
 * The proof that an address was verified, between the screen that earned it and
 * the screen that spends it.
 *
 * A Firebase custom token and the uid it opens. `handleNameNext` spends it:
 * the account cannot be created before the name is given, so the proof has to
 * outlive the code screen. On mobile that is two slides of one component and a
 * `useState` was enough. On desktop the code is entered in the auth gate modal,
 * on another route entirely, so the two screens no longer share a component.
 *
 * IN MEMORY, and that is the whole point:
 *
 * - NOT `sessionStorage`, because this is a bearer credential for an account.
 *   The onboarding flow store is session-backed and deliberately keeps the
 *   password and the typed code out for the same reason.
 * - NOT router state. `history.state` is durable on Chrome desktop — it
 *   survives a reload, a tab restore and a browser restart
 *   (`.knowledge/router-state-command-replay.md`) — which is the opposite of
 *   what a credential wants.
 *
 * Losing it on reload is therefore correct, and it is self-healing: nobody is
 * signed in without it, so the anonymous route guard reopens the modal and the
 * practitioner asks for a fresh code.
 */
export type OtpCredential = {
  customToken: string;
  uid: string;
  /** The address the code proved, which is what the account is created with. */
  email: string;
};

const store = memoryStore<OtpCredential | null>(null);

export const otpCredentialStore = store;

export function useOtpCredential(): [
  OtpCredential | null,
  (value: OtpCredential | null) => void,
] {
  const [value, set] = useStore(store);
  return [value, set];
}
